'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleRight,
  faExternalLinkAlt,
  faPlus,
  faQuestionCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { PulseButton, Tooltip } from 'src/components/PulseUI';
import { TypeIcon, AddToListModal } from 'src/components';
import { useLogging } from 'src/context/logging';
import { sentenceCase } from 'src/utils/sentenceCase/sentenceCase';
import Link from 'next/link';
import { getProfileUrl } from 'src/utils/profileUrl';
import { formatSocClassification } from 'src/components/Profiles/components/ProfileDetails/socTypes';
import CompactSchedule from './CompactSchedule';

interface CompactLocationByDayProps {
  isFirst?: boolean;
  expanded: string[];
  setExpanded: React.Dispatch<React.SetStateAction<string[]>>;
  location: Record<string, any>;
}

const CompactLocationByDay: React.FC<CompactLocationByDayProps> = ({
  location,
  isFirst = false,
  expanded,
  setExpanded,
}) => {
  const log = useLogging();
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const providers = [location];

  useEffect(() => {
    if (isFirst) {
      setExpanded([location.provider_id]);
    }
  }, [isFirst]);

  const handleAccordionChange = (locationId: string) => {
    setExpanded((prevExpanded) => {
      if (prevExpanded.includes(locationId)) {
        return prevExpanded.filter((id) => id !== locationId);
      } else {
        return [...prevExpanded, locationId];
      }
    });
  };

  const formatLocationType = (type: string) => {
    return type === 'center' ? 'Hospital' : sentenceCase(type?.replace(/_/g, ' '))
  };

  const handleAddToListSuccess = useCallback(
    (result: { addedCount: number }) => {
      log.event('addProvider', {
        source: 'Location By Day',
        count: result.addedCount,
      });
    },
    [log]
  );

  const isLessThanEleven = location?.claims && location?.claims === '<11';
  const isExpanded = expanded.includes(location.provider_id);

  return (
    <div className="pb-2">
      <div
        id={`volume-by-location-accordion-${location.provider_id}`}
        className="border border-neutral-300 rounded-lg overflow-hidden"
      >
        {/* Accordion Header */}
        <div className="flex min-h-16 justify-between items-stretch w-full overflow-hidden">
          {/* Left Side Container */}
          <div className="flex relative flex-1 items-center h-full w-full group">
            {/* Left Side */}
            <div
              className="flex items-center gap-3 h-full px-4 cursor-pointer flex-1"
              onClick={() => handleAccordionChange(location.provider_id)}
              data-testid={`accordion-toggle-${location.provider_id}`}
            >
              {/* Expand/collapse icon */}
              <div className="flex justify-center items-center h-6 w-6 bg-neutral-100 rounded-full flex-shrink-0">
                <Icon
                  icon={isExpanded ? faAngleDown : faAngleRight}
                  className="text-neutral-600"
                />
              </div>
              <TypeIcon
                type={location.soc_type || location.type}
                size="xl"
                className="text-sky-800"
              />
              <div className="h-full w-full flex flex-col justify-center py-2">
                <Link
                  id={`volume-by-location-provider-${location.provider_id}`}
                  href={getProfileUrl(location.type, location.provider_id)}
                  scroll={false}
                  onClick={(e) => {
                    e.stopPropagation();
                    log.event('Location Clicked', {
                      source: 'web',
                      location: 'VolumeByLocation',
                      provider_id: location.provider_id,
                    });
                  }}
                  className="flex items-center text-sky-900 no-underline"
                >
                  <span className="text-sm text-link font-medium">
                    {location.name}
                  </span>
                  <Icon icon={faExternalLinkAlt} className="pl-1 text-xs" />
                </Link>
                <div className="flex gap-2 items-center justify-start w-full">
                  <span className="text-neutral-600 text-xs font-medium">
                    {location.soc_type
                      ? formatSocClassification(location)
                      : formatLocationType(location.type)}
                  </span>
                </div>
              </div>
            </div>
            {/* Hover Add Button */}
            <div
              id={`volume-by-location-add-provider-${location.provider_id}`}
              className="absolute top-1/2 right-2 -translate-y-1/2"
            >
              <PulseButton
                className="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-300 shadow-sm bg-white rounded-full"
                variant="outline"
                color="neutral"
                startDecorator={faPlus}
                onClick={() => setShowProviderDialog(true)}
              >
                Add
              </PulseButton>
            </div>
          </div>
          {/* Right Side - Green percentage sidebar */}
          <div className="flex px-4 flex-col justify-center bg-green-100 w-24 shrink-0 -my-px">
            <div className="flex flex-col items-end">
              <Tooltip
                content="Data can be any number 1 to 10"
                position="top"
                showOnCondition={isLessThanEleven}
              >
                <span
                  className={`text-base text-green-800 font-semibold ${
                    isLessThanEleven ? 'cursor-pointer' : 'cursor-default'
                  }`}
                >
                  {location?.claims &&
                    Number(location?.claims)?.toLocaleString()}
                </span>
              </Tooltip>
              <Tooltip content="of total Site volume" position="top">
                {location?.percentage ? (
                  <span className="text-base text-green-800 font-normal cursor-pointer">
                    {location?.percentage}%
                  </span>
                ) : (
                  <Icon
                    icon={faQuestionCircle}
                    className="text-sm text-neutral-600"
                  />
                )}
              </Tooltip>
            </div>
          </div>
        </div>
        {/* Accordion Details - Schedule */}
        {isExpanded && location.schedule && (
          <div>
            <CompactSchedule schedule={location.schedule} />
          </div>
        )}
      </div>
      <AddToListModal
        isOpen={showProviderDialog}
        onClose={() => setShowProviderDialog(false)}
        mode="add"
        providers={providers}
        onSuccess={handleAddToListSuccess}
      />
    </div>
  );
};

export default CompactLocationByDay;
